import React, { useState } from 'react';
import { 
  Box, Grid, TextField, Button, Typography, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import apiClient from '../ApiClient/ApiClient';

// 建立模式用欄位
const buildColumns = [
  { field: 'drawNum', headerName: '圖號', width: 150, editable: true },
  { field: 'piNum', headerName: '批號', width: 150, editable: true },
  { field: 'quantity', headerName: '數量', width: 120, editable: true },
  { field: 'client', headerName: '客戶', width: 150, editable: true },
];

const getDefectiveRowId = (row) => row.Id ?? row.id;

const normalizeDefectiveRow = (x, idx = 0) => {
  const id = x.Id ?? x.id ?? idx + 1;
  const drawNum = x.項次
    ? `${x.圖號 ?? x.drawNum ?? ''}-${x.項次}`
    : (x.圖號 ?? x.drawNum ?? '');

  return {
    id,
    Id: Number(id),
    client: x.客戶 ?? x.client ?? '',
    piNum: x.批號 ?? x.piNum ?? '',
    drawNum,
    itemName: x.物品名 ?? x.itemName ?? '',
    quantity: x.不良數 ?? x.defectiveQty ?? x.quantity ?? 0,
    replenishQty: x.補交數 ?? x.replenishQty ?? 0,
    date: x.日期 ?? x.date ?? '',
    orderNum: x.訂單號碼 ?? x.orderNum ?? '',
    serialNum: x.流水號 ?? x.serialNum ?? '',
    company: x.承製公司 ?? x.company ?? '',
  };
};

export default function DefectiveRegistration() {
  const [formData, setFormData] = useState({ drawNum: '', quantity: '', client: '' });
  const [gridData, setGridData] = useState([]);
  const [responseMessage, setResponseMessage] = useState('');
  const [piNum, setPiNum] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // 搜尋視窗
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDrawNum, setSearchDrawNum] = useState('');
  const [searchStart, setSearchStart] = useState(dayjs());
  const [searchEnd, setSearchEnd] = useState(dayjs());

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const fetchPiNum = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/OrderManage/GetPiNum`);
      if (response.status === 200 && response.data) return response.data.piNum;
      throw new Error('未能獲取批號');
    } catch (error) {
      console.error(error);
      alert('獲取批號失敗，請稍後再試。');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const { drawNum, quantity, client } = formData;
    if (!drawNum || !quantity || !client) {
      alert('請填寫所有欄位！');
      return;
    }
    setIsSearchMode(false);
    setResponseMessage('');

    let currentPiNum = piNum;
    if (gridData.length === 0) {
      const newPiNum = await fetchPiNum();
      if (!newPiNum) return;
      currentPiNum = newPiNum;
      setPiNum(currentPiNum);
    }

    const newEntry = {
      id: uuidv4(),
      piNum: currentPiNum || '暫無批號',
      drawNum,
      quantity: parseInt(quantity, 10),
      client,
    };

    setGridData((prev) => [...prev, newEntry]);
    setFormData({ drawNum: '', quantity: '', client: '' });
  };

  const handleSave = async () => {
    if (isSearchMode) {
      alert('目前為搜尋結果，無法保存。請先「重置」或用「建立」產生資料。');
      return;
    }
    if (gridData.length === 0) {
      alert('表格中沒有資料可以儲存！');
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.post(`/OrderManage/RegisterBulk`, gridData);
      if (response.status === 200) {
        setResponseMessage('儲存成功！');
        const list = Array.isArray(response.data)
          ? response.data.map(normalizeDefectiveRow)
          : [];
        setGridData(list);
        if (response.data?.piNum) setPiNum(response.data.piNum);
      } else {
        setResponseMessage('儲存失敗，請稍後再試。');
      }
    } catch (error) {
      setResponseMessage('儲存失敗，請稍後再試。');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openSearchDialog = () => setSearchOpen(true);

  const handleSearchSubmit = async () => {
    if (!searchStart || !searchEnd) {
      alert('請選擇起始日期與結束日期');
      return;
    }
    const start = dayjs(searchStart).format('YYYY/MM/DD');
    const end = dayjs(searchEnd).format('YYYY/MM/DD');
    const params = {
      drawNum: (searchDrawNum || '').trim(),
      start,
      end,
    };
    setLoading(true);
    setResponseMessage('');
    try {
      // 若你的 API 不同，換這條即可
      const resp = await apiClient.get(`/OrderManage/SearchDefective`, { params });

      // 將回傳資料映射成你要顯示的中文欄位
      const raw = Array.isArray(resp.data) ? resp.data : (resp.data?.items ?? []);
      const rows = raw.map((x, idx) => ({
        // Id 優先用後端 Id，沒有就給一個穩定的 fallback
        Id: x.Id ?? x.id ?? idx + 1,

        // 以下欄位盡量容錯（同義欄位也接）
        客戶: x.客戶 ?? x.client ?? '',
        批號: x.批號 ?? x.piNum ?? '',
        図號: undefined, // 防止手滑寫錯，下一行才是正確欄位
        圖號: x.圖號 ?? x.drawNum ?? '',
        項次: x.項次 ?? x.itemNum ?? '',
        不良數: x.不良數 ?? x.defectiveQty ?? x.quantity ?? 0,
        補交數: x.補交數 ?? x.replenishQty ?? 0,
        日期: x.日期 ?? x.date ?? '',
        訂單號碼: x.訂單號碼 ?? x.orderNum ?? '',
        流水號: x.流水號 ?? x.serialNum ?? '',
        物品名: x.物品名 ?? x.itemName ?? '',
        承製公司: x.承製公司 ?? x.company ?? '',
      }));

      setGridData(raw.map(normalizeDefectiveRow));
      setIsSearchMode(true);
      setResponseMessage(`搜尋完成，共 ${rows.length} 筆。`);
      setSearchOpen(false);
    } catch (err) {
      console.error(err);
      setGridData([]);
      setIsSearchMode(true);
      setResponseMessage('搜尋失敗，請稍後再試。');
      setSearchOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setGridData([]);
    setPiNum('');
    setResponseMessage('');
    setIsSearchMode(false);
  };

  const processRowUpdate = async (newRow, oldRow) => {
    const normalizedRow = {
      ...newRow,
      drawNum: (newRow.drawNum || '').trim(),
      piNum: (newRow.piNum || '').trim(),
      client: (newRow.client || '').trim(),
      quantity: Number(newRow.quantity) || 0,
    };

    if (!normalizedRow.drawNum || !normalizedRow.piNum || !normalizedRow.client) {
      throw new Error('圖號、批號、客戶不可空白');
    }

    if (normalizedRow.quantity < 0) {
      throw new Error('不良數不可小於 0');
    }

    if (!isSearchMode || !Number(normalizedRow.Id)) {
      setGridData((prev) =>
        prev.map((row) =>
          getDefectiveRowId(row) === getDefectiveRowId(oldRow) ? normalizedRow : row
        )
      );
      return normalizedRow;
    }

    setLoading(true);
    try {
      const response = await apiClient.put(`/OrderManage/Defective/${normalizedRow.Id}`, {
        id: String(normalizedRow.Id),
        drawNum: normalizedRow.drawNum,
        piNum: normalizedRow.piNum,
        quantity: normalizedRow.quantity,
        client: normalizedRow.client,
      });

      const savedRow = normalizeDefectiveRow(response.data);
      setGridData((prev) =>
        prev.map((row) =>
          getDefectiveRowId(row) === getDefectiveRowId(oldRow) ? savedRow : row
        )
      );
      setResponseMessage('修改成功。');
      return savedRow;
    } catch (error) {
      setResponseMessage(error?.response?.data?.message || '修改失敗，請稍後再試。');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRowUpdateError = (error) => {
    console.error(error);
    alert(error?.response?.data?.message || error?.message || '修改失敗，請稍後再試。');
  };

  const handleDelete = async (row) => {
    if (!row?.Id) {
      setGridData((prev) => prev.filter((item) => getDefectiveRowId(item) !== getDefectiveRowId(row)));
      return;
    }

    if (!window.confirm('確定要刪除這筆不良品資料？')) {
      return;
    }

    setLoading(true);
    try {
      await apiClient.delete(`/OrderManage/Defective/${row.Id}`);
      setGridData((prev) => prev.filter((item) => getDefectiveRowId(item) !== getDefectiveRowId(row)));
      setResponseMessage('刪除成功。');
    } catch (error) {
      setResponseMessage(error?.response?.data?.message || '刪除失敗，請稍後再試。');
      console.error(error);
      alert(error?.response?.data?.message || '刪除失敗，請稍後再試。');
    } finally {
      setLoading(false);
    }
  };

  const defectiveColumns = [
    { field: 'client', headerName: '客戶', width: 100, editable: true },
    { field: 'piNum', headerName: '批號', width: 120, editable: true },
    { field: 'drawNum', headerName: '圖號', width: 140, editable: true },
    { field: 'itemName', headerName: '物品名', width: 160, flex: 1 },
    { field: 'quantity', headerName: '不良數', width: 100, type: 'number', editable: true },
    { field: 'replenishQty', headerName: '補交數', width: 100 },
    { field: 'date', headerName: '日期', width: 120 },
    { field: 'orderNum', headerName: '訂單號碼', width: 120 },
    { field: 'serialNum', headerName: '流水號', width: 100 },
    { field: 'company', headerName: '承製公司', width: 110 },
    { field: 'Id', headerName: 'Id', width: 80 },
    {
      field: 'actions',
      headerName: '操作',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          color="error"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => handleDelete(params.row)}
        >
          刪除
        </Button>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ flexGrow: 1, padding: 3 }}>
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <Typography variant="h4" gutterBottom>
          不良品登錄
        </Typography>

        <Paper sx={{ padding: 2, marginBottom: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="圖號"
                variant="outlined"
                name="drawNum"
                value={formData.drawNum}
                onChange={handleInputChange}
                fullWidth
                required={!isSearchMode}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="數量"
                variant="outlined"
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleInputChange}
                fullWidth
                required={!isSearchMode}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="客戶"
                variant="outlined"
                name="client"
                value={formData.client}
                onChange={handleInputChange}
                fullWidth
                required={!isSearchMode}
              />
            </Grid>

            <Grid item xs={12} sm={12} className="d-flex align-items-center justify-content-between">
              <Typography variant="h6">
                批號: {piNum || '尚未生成'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={openSearchDialog}
                >
                  搜尋
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleAdd}
                >
                  建立
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ padding: 2, marginBottom: 3 }}>
          <Typography variant="h6" gutterBottom>
            {isSearchMode ? '搜尋結果' : '建立結果'}
          </Typography>
          <div style={{ height: 360, width: '100%' }}>
            <DataGrid
              rows={gridData}
              columns={isSearchMode ? defectiveColumns : buildColumns}
              // 兼容：搜尋結果用 Id，建立模式用 id
              getRowId={getDefectiveRowId}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              rowHeight={32}
              headerHeight={34}
              sx={{ '& .MuiDataGrid-cell': { fontSize: 14 } }}
            />
          </div>
        </Paper>

        <Box className="d-flex justify-content-end" sx={{ marginBottom: 3, gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            endIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSearchMode}
          >
            保存
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleReset}
          >
            重置
          </Button>
        </Box>

        {responseMessage && (
          <Typography variant="body1" color="textPrimary">
            {responseMessage}
          </Typography>
        )}

        {/* 搜尋條件 Dialog */}
        <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>搜尋條件</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="圖號"
                  fullWidth
                  size="small"
                  value={searchDrawNum}
                  onChange={(e) => setSearchDrawNum(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="起始日期"
                  value={searchStart}
                  onChange={(v) => setSearchStart(v)}
                  format="YYYY/MM/DD"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="結束日期"
                  value={searchEnd}
                  onChange={(v) => setSearchEnd(v)}
                  format="YYYY/MM/DD"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSearchOpen(false)}>取消</Button>
            <Button variant="contained" onClick={handleSearchSubmit}>查詢</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
